import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { useAuditoresPagination } from "../../../auditoria/presentation/hooks/Auditoria_auditores/pagination";
import { DashboardObras } from "../../domain/models/dashboard_obras";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { initialObrasListaFilter, useObrasListaFilter } from "../hooks/Obras_lista/filter";
import { useObrasListaPagination } from "../hooks/Obras_lista/pagination";

import CommonConstants from "../../../../common/constants";

/**
 * LoadPrincipalListaFuncState
 */

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};

/**
 * usePrincipalObrasViewModel
 */

export const usePrincipalObrasViewModel = () => {
  const {
    commons: {
      getAllListagemDocUseCase,
      getGedArquivoDataUseCase,
    },
    obras: {
      getDashboardObrasInformationUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [dashboardObrasInformation, setDashboardObrasInformation] = useState<DashboardObras>();

  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

  const pagination = useAuditoresPagination();

  /**
   * Funcoes de carregamento
   */

  const loadDashboardObrasInformation = useCallback(async () => {
    try {
      const result = await getDashboardObrasInformationUseCase.getDashboardObrasInformation();
      setDashboardObrasInformation(result);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar informações do dashboard!");
    }
  }, [
    getDashboardObrasInformationUseCase,
  ]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      await getAllListagemDocUseCase.getAllListagemDoc(
        String(CommonConstants.moduloDocumentos.Obras),
        filteredPage,
        filteredRowsPerPage
      );
      setDocLista(getAllListagemDocUseCase.items);
      pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
      pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar documentos!");
    }
  }, [
    getAllListagemDocUseCase,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadDashboardObrasInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao carregar os dados. Tente novamente.')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(undefined);
      await loadDashboardObrasInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao recarregar os dados. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  }, [
    loadDashboardObrasInformation,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleCardClick = useCallback((cardType: string) => {
    switch (cardType) {
      case 'equipamentosCalibrados':
        useObrasListaFilter.setState({ ...initialObrasListaFilter, statusEquipamentos: 1 });
        useObrasListaPagination.setState({ page: 1 });
        backOrNavigate('/Obras_lista');
        break;

      case 'equipamentosVerificados':
        useObrasListaFilter.setState({ ...initialObrasListaFilter, statusEquipamentos: 2 });
        useObrasListaPagination.setState({ page: 1 });
        backOrNavigate('/Obras_lista');
        break;

      case 'equipamentosEmAtraso':
        useObrasListaFilter.setState({ ...initialObrasListaFilter, statusEquipamentos: 3 });
        useObrasListaPagination.setState({ page: 1 });
        backOrNavigate('/Obras_lista');
        break;

      case 'documentosVencidos':
        useObrasListaFilter.setState({ ...initialObrasListaFilter, statusDocumentos: 2 });
        useObrasListaPagination.setState({ page: 1 });
        backOrNavigate('/Obras_lista');
        break;

      case 'equipamentosLocadosEmAtraso':
        useObrasListaFilter.setState({ ...initialObrasListaFilter, statusEquipamentos: 4 });
        useObrasListaPagination.setState({ page: 1 });
        backOrNavigate('/Obras_lista');
        break;

      case 'resultadoConcretagemPendenteRegistro':
        useObrasListaFilter.setState({ ...initialObrasListaFilter, resistenciaCompressao: 1 });
        useObrasListaPagination.setState({ page: 1 });
        backOrNavigate('/Obras_lista');
        break;
    }
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadDocListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  return {
    loading,
    errorMessage,
    dashboardObrasInformation: dashboardObrasInformation ?? null,
    docLista,
    pagination,
    handleReload,
    handleCardClick,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadFile,
  };
}